if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.select = this.querySelector("select.dropdown-collection");
      this.input = this.querySelector("input.input-collection");
      this.selects = this.querySelectorAll("select.dropdown-filter");

      this.setupEventListeners();

      if (!this.select) {
        this.populateCollectionFilters(true);
      }

      this.form.setAttribute("action", this.constructURL());

      if (!this.hasAttribute("required-section-for-collection-and-search")) {
        this.button.disabled = false;
      }
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        window.location = this.form.getAttribute("action");
      });
      this.select?.addEventListener("change", this.onCollectionChange.bind(this));
      this.selects.forEach((select) => {
        select.addEventListener("change", this.onFilterChange.bind(this));
      });
    }

    constructURL() {
      const collectionUrl =
        this.select?.value ?? this.input?.value ?? window.routes.all_products_url;
      const url = new URL(collectionUrl, window.requestShopUrl);

      this.querySelectorAll("select.dropdown-filter:enabled").forEach((select) => {
        if (select.value === "") {
          return;
        }

        Array.from(select.selectedOptions).forEach((option) => {
          url.searchParams.set(option.dataset.paramName, option.value);
        });
      });

      return url.toString();
    }

    updateButtonState() {
      if (!this.hasAttribute("required-section-for-collection-and-search")) {
        return;
      }

      const firstSelect = Array.from(this.selects).find(
        (select) => Number(select.dataset.index) === 1,
      );
      this.button.disabled = !firstSelect || firstSelect.value === "";
    }

    setFormUrl(url, background) {
      this.form.setAttribute("action", url);

      if (!background) {
        if (this.hasAttribute("required-section-for-collection-and-search")) {
          this.updateButtonState();
        } else {
          this.button.disabled = false;
        }
      }
    }

    selectRemoveOptions(select) {
      select.querySelectorAll("option:not(.default)").forEach((option) => {
        select.removeChild(option);
      });

      select.querySelector("option").selected = true;
    }

    onFilterChange(event) {
      const selectedIndex = event.target.dataset.index;

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= Number(selectedIndex)) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.setFormUrl(this.constructURL());

      if (event.target.value === "") {
        return;
      }

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");
      this.toggleLoading();

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          this.toggleLoading();
          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          this.selects.forEach((select) => {
            if (Number(select.dataset.index) <= Number(selectedIndex)) {
              return;
            }

            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );

            if (options.length > 0) {
              const seen = new Set();
              const availableOptions = Array.from(options).filter((option) => {
                if (Number(option.dataset.count) <= 0) {
                  return false;
                }

                const key = option.value.trim().toLowerCase();
                if (seen.has(key)) {
                  return false;
                }

                seen.add(key);
                return true;
              });

              select.append(...availableOptions);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });

          const nextSelect = Array.from(this.selects).find(
            (select) => Number(select.dataset.index) === Number(selectedIndex) + 1,
          );

          if (nextSelect && nextSelect.dataset.activeValue && !nextSelect.disabled) {
            const match = Array.from(nextSelect.options).find(
              (option) => option.value === nextSelect.dataset.activeValue,
            );

            if (match) {
              match.selected = true;
              nextSelect.dispatchEvent(new Event("change"));
            }
          }
        })
        .catch((error) => {
          throw error;
        });
    }

    populateCollectionFilters(background = false) {
      if (this._populatingFilters) {
        return;
      }

      this._populatingFilters = true;
      this.setFormUrl(this.constructURL(), background);

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");

      if (!background) {
        this.toggleLoading();
      }

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          if (!background) {
            this.toggleLoading();
          }

          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          this.selects.forEach((select) => {
            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );

            if (options.length > 0) {
              const seen = new Set();
              const uniqueOptions = Array.from(options).filter((option) => {
                const key = option.value.trim().toLowerCase();
                if (seen.has(key)) {
                  return false;
                }

                seen.add(key);
                return true;
              });

              select.append(...uniqueOptions);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });

          const firstSelect = Array.from(this.selects).find(
            (select) => Number(select.dataset.index) === 1,
          );

          if (firstSelect && firstSelect.dataset.activeValue) {
            const match = Array.from(firstSelect.options).find(
              (option) => option.value === firstSelect.dataset.activeValue,
            );

            if (match) {
              match.selected = true;
              firstSelect.dispatchEvent(new Event("change"));
            }
          }

          this.updateButtonState();
        })
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          this._populatingFilters = false;
        });
    }

    onCollectionChange() {
      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.populateCollectionFilters();
    }

    toggleLoading() {
      this.classList.toggle("is-loading");
      this.button.classList.toggle("loading");
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}
